import { useCallback } from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { type CellDragPayload, serializeCellDragPayload, moveGridCell } from './gridCellDnd';
import {
    type GridAttrs,
    type CellData,
    type Preset,
    MIN_COLS,
    MAX_COLS,
    MIN_ROW_HEIGHT,
    MAX_ROW_HEIGHT,
    emptyCell,
    buildEvenWidths,
    normalizeCells,
    getUploadHandler,
    rebuildGrid
} from './imageGridShared';

interface UseImageGridActionsParams {
    editor: NodeViewProps['editor'];
    getPos: NodeViewProps['getPos'];
    node: NodeViewProps['node'];
    attrs: GridAttrs;
    cells: CellData[];
}

/**
 * All state-mutating handlers for the image grid NodeView: cell content,
 * captions, layout (rows/cols/presets/height), and drag-move. Kept separate
 * from ImageGridView's JSX so the NodeView component itself stays focused on
 * rendering.
 */
export function useImageGridActions({
    editor,
    getPos,
    node,
    attrs,
    cells
}: UseImageGridActionsParams) {
    const applyChange = useCallback(
        (newAttrs: Partial<GridAttrs>, newCells: CellData[]) => {
            rebuildGrid(editor, getPos as () => number | undefined, node, newAttrs, newCells);
        },
        [editor, getPos, node]
    );

    const handleCellFilled = useCallback(
        (idx: number, src: string, fileId: string) => {
            applyChange(
                {},
                cells.map((c, i) => (i === idx ? { ...c, src, fileId, caption: '' } : c))
            );
        },
        [cells, applyChange]
    );

    const handleCellRemoved = useCallback(
        (idx: number) => {
            applyChange(
                {},
                cells.map((c, i) => (i === idx ? emptyCell() : c))
            );
        },
        [cells, applyChange]
    );

    const handleCellCaption = useCallback(
        (idx: number, caption: string) => {
            applyChange(
                {},
                cells.map((c, i) => (i === idx ? { ...c, caption } : c))
            );
        },
        [cells, applyChange]
    );

    const handleColResize = useCallback(
        (updated: number[]) => {
            applyChange({ colWidths: updated }, cells);
        },
        [cells, applyChange]
    );

    // Shared upload-then-fill path. Resolves once the cell is updated, or once
    // the upload handler is absent or the upload fails (the cell is left in
    // its previous state on failure).
    const uploadAndFill = useCallback(
        async (cellIndex: number, file: File): Promise<void> => {
            const onImageUpload = getUploadHandler(editor);
            if (!onImageUpload) return;
            try {
                const { url, fileId } = await onImageUpload(file);
                handleCellFilled(cellIndex, url, fileId);
            } catch {
                // cell stays as-is on upload failure
            }
        },
        [editor, handleCellFilled]
    );

    const handleDropFile = useCallback(
        (cellIndex: number, file: File) => {
            void uploadAndFill(cellIndex, file);
        },
        [uploadAndFill]
    );

    // Drag source: serialise a filled cell so it can be moved into another cell
    // or grid. Returns null for empty cells (nothing to move).
    const buildCellDragPayload = useCallback(
        (cellIndex: number): string | null => {
            const pos = getPos();
            if (pos === undefined) return null;
            const c = cells[cellIndex];
            if (!c?.src || !c.fileId) return null;
            return serializeCellDragPayload({
                gridPos: pos,
                cellIndex,
                src: c.src,
                fileId: c.fileId,
                caption: c.caption
            });
        },
        [getPos, cells]
    );

    const handleMoveCell = useCallback(
        (targetCellIndex: number, payload: CellDragPayload) => {
            const targetPos = getPos();
            if (targetPos === undefined) return;
            const cell = cells[targetCellIndex] ?? emptyCell();
            moveGridCell(editor, payload, { gridPos: targetPos, cellIndex: targetCellIndex, cell });
        },
        [editor, getPos, cells]
    );

    const handleAddRow = useCallback(() => {
        const newRows = attrs.rows + 1;
        applyChange({ rows: newRows }, normalizeCells(cells, newRows * attrs.cols));
    }, [attrs, cells, applyChange]);

    const handleRemoveRow = useCallback(() => {
        if (attrs.rows <= 1) return;
        const newRows = attrs.rows - 1;
        applyChange({ rows: newRows }, normalizeCells(cells, newRows * attrs.cols));
    }, [attrs, cells, applyChange]);

    const handleAddCol = useCallback(() => {
        if (attrs.cols >= MAX_COLS) return;
        const newCols = attrs.cols + 1;
        const newWidths = buildEvenWidths(newCols);
        const newCells: CellData[] = [];
        for (let r = 0; r < attrs.rows; r++) {
            for (let c = 0; c < newCols; c++) {
                newCells.push(c < attrs.cols ? cells[r * attrs.cols + c] : emptyCell());
            }
        }
        applyChange({ cols: newCols, colWidths: newWidths }, newCells);
    }, [attrs, cells, applyChange]);

    const handleRemoveCol = useCallback(() => {
        if (attrs.cols <= MIN_COLS) return;
        const newCols = attrs.cols - 1;
        const newWidths = buildEvenWidths(newCols);
        const newCells: CellData[] = [];
        for (let r = 0; r < attrs.rows; r++) {
            for (let c = 0; c < newCols; c++) {
                newCells.push(cells[r * attrs.cols + c]);
            }
        }
        applyChange({ cols: newCols, colWidths: newWidths }, newCells);
    }, [attrs, cells, applyChange]);

    const handleHeightChange = useCallback(
        (h: number) => {
            applyChange(
                { rowHeight: Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, h)) },
                cells
            );
        },
        [cells, applyChange]
    );

    const handleDeleteGrid = useCallback(() => {
        const pos = getPos();
        if (pos === undefined) return;
        const currentNode = editor.state.doc.nodeAt(pos);
        if (!currentNode) return;
        editor.view.dispatch(editor.state.tr.delete(pos, pos + currentNode.nodeSize));
    }, [editor, getPos]);

    const handlePreset = useCallback(
        (preset: Preset) => {
            const newCount = preset.rows * preset.cols;
            const oldCount = attrs.rows * attrs.cols;
            const preserved = Math.min(oldCount, newCount);
            const newCells: CellData[] = [
                ...cells.slice(0, preserved),
                ...Array.from({ length: newCount - preserved }, emptyCell)
            ];
            applyChange(
                { rows: preset.rows, cols: preset.cols, colWidths: buildEvenWidths(preset.cols) },
                newCells
            );
        },
        [attrs, cells, applyChange]
    );

    return {
        handleCellFilled,
        handleCellRemoved,
        handleCellCaption,
        handleColResize,
        uploadAndFill,
        handleDropFile,
        buildCellDragPayload,
        handleMoveCell,
        handleAddRow,
        handleRemoveRow,
        handleAddCol,
        handleRemoveCol,
        handleHeightChange,
        handleDeleteGrid,
        handlePreset
    };
}
